import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Wallet, Loader2, Plus } from "lucide-react";
import { api, formatApiError } from "@/api/client";

const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
    draft: { bg: "var(--neutral-bg)", fg: "var(--neutral-fg)" },
    review: { bg: "var(--warning-bg)", fg: "var(--warning-fg)" },
    approved: { bg: "var(--info-bg)", fg: "var(--info-fg)" },
    paid: { bg: "var(--success-bg)", fg: "var(--success-fg)" },
    voided: { bg: "var(--error-bg)", fg: "var(--error-fg)" },
};

function money(m: number, currency = "AZN", lang = "az") {
    return new Intl.NumberFormat(lang, { style: "currency", currency }).format((m || 0) / 100);
}

export default function PayrollRuns() {
    const { t, i18n } = useTranslation();
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/payroll/runs");
            setRows(r.data.items || []);
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const createRun = async () => {
        setCreating(true); setErr(null);
        try {
            const now = new Date();
            await api.post("/payroll/runs", { period_year: now.getFullYear(), period_month: now.getMonth() + 1 });
            await load();
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setCreating(false); }
    };

    return (
        <div className="space-y-4" data-testid="payroll-runs-page">
            <header className="flex items-center gap-3">
                <Wallet size={22} style={{ color: "var(--primary)" }} />
                <h1 className="text-xl font-semibold flex-1" style={{ color: "var(--text)" }}>{t("payroll.runs.title")}</h1>
                <button data-testid="payroll-run-new-btn" onClick={createRun} disabled={creating}
                    className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors"
                    style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                    <Plus size={16} /> {t("payroll.runs.create_current_month")}
                </button>
            </header>
            <div className="rounded-md p-3 text-xs" style={{ background: "var(--info-bg)", color: "var(--info-fg)" }} data-testid="payroll-runs-explain">
                <div className="font-semibold mb-1">{t("payroll.runs.explain.title")}</div>
                <div>{t("payroll.runs.explain.body")}</div>
            </div>
            {loading && <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>}
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
            {!loading && !rows.length && <div className="text-sm" style={{ color: "var(--text-muted)" }}>{t("payroll.runs.empty")}</div>}
            {!!rows.length && (
                <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                    <table className="w-full text-sm">
                        <thead>
                            <tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                <th className="text-left px-3 py-2">{t("payroll.run.period")}</th>
                                <th className="text-left px-3 py-2">{t("payroll.run.status")}</th>
                                <th className="text-right px-3 py-2">{t("payroll.run.items")}</th>
                                <th className="text-right px-3 py-2">{t("payroll.run.total")}</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r: any) => {
                                const tone = STATUS_TONE[r.status] || STATUS_TONE.draft;
                                return (
                                    <tr key={r.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`payroll-run-row-${r.id}`}>
                                        <td className="px-3 py-2" style={{ color: "var(--text)" }}>{r.period_year}-{String(r.period_month).padStart(2, "0")}</td>
                                        <td className="px-3 py-2">
                                            <span className="rounded-full px-2 py-0.5 text-xs inline-flex items-center gap-1" style={{ background: tone.bg, color: tone.fg }} data-testid={`payroll-run-status-${r.id}`}>
                                                {String(t([`payroll.status.${r.status}`, r.status]))}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right" style={{ color: "var(--text-muted)" }}>{r.items_count ?? 0}</td>
                                        <td className="px-3 py-2 text-right font-medium" style={{ color: "var(--text)" }}>{money(r.items_total_minor || 0, "AZN", i18n.language)}</td>
                                        <td className="px-3 py-2 text-right"><Link to={`/hr/payroll/${r.id}`} className="text-xs font-medium hover:underline" style={{ color: "var(--primary)" }} data-testid={`payroll-run-open-${r.id}`}>{t("common.open")}</Link></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
